import { getClientRouteOptions } from "helpers/regionDryingPeriods";
import { queryParams } from "helpers/reqparams";
import { alertRequest, body, request } from "services/request";

const resource = "api/organizationProfile";

type BankLookupItem = {
    id?: string | number;
    value?: string;
    unrestricted_value?: string;
    displayName?: string;
    fullAddress?: string;
    bank?: string;
    bankId?: string;
    bic?: string;
    ks?: string;
    rs?: string;
    correspondentAccount?: string;
    correspondent_account?: string;
    settlementAccount?: string;
    data?: {
        bic?: string;
        correspondent_account?: string;
        name?: {
            payment?: string;
            full?: string;
            short?: string;
        };
    };
};

type BanksSearchFilter = {
    dataIndex?: string | string[];
    value?: string | string[] | number | number[];
};

type BanksSearchParams = {
    start?: number;
    length?: number;
    filters?: BanksSearchFilter[];
};

export type KonturIntegrationState = {
    isAuthenticated: boolean;
    updatedAt: string | null;
    email: string | null;
    isReady: boolean;
    issue: string | null;
};

export type KonturIntegrationResponse = KonturIntegrationState | false | undefined;

const normalizeBankLookupItem = (item: BankLookupItem) => {
    const bankId = item.bankId || item.bic || item.data?.bic || "";
    const bank = item.bank || item.displayName || item.data?.name?.payment || item.data?.name?.full || item.value || "";
    const ks =
        item.ks || item.correspondentAccount || item.correspondent_account || item.data?.correspondent_account || "";
    const rs = item.rs || item.settlementAccount || "";

    return {
        ...item,
        id: bankId || item.id || item.value,
        bankId,
        bank,
        ks,
        rs,
    };
};

const getBanksSearchKey = (params: BanksSearchParams) => {
    const rawKey = params.filters?.[0]?.value;
    const key = Array.isArray(rawKey) ? rawKey[0] : rawKey;

    return key === undefined || key === null ? "" : String(key);
};

export const showOrg = () =>
    Promise.all([
        request(`${resource}/get`, { method: "GET" }),
        request(`${resource}/getCurrency`, { method: "GET" }),
    ]).then(([organization, currency]) => ({
        ...organization,
        currencyData: { ...currency, name: currency.name.replace(".", "") },
    }));

export const getInfoToCreateDocument = () => request(`${resource}/getInfoToCreateDocument`, { method: "GET" });
export const editInfoToCreateDocument = (params: {}) => request(`${resource}/editInfoToCreateDocument`, body(params));
export const getBanks = (params: BanksSearchParams) => {
    const key = getBanksSearchKey(params);
    if (!key) {
        return Promise.resolve([]);
    }

    return request(`${resource}/getBanks${queryParams({ key })}`, { method: "GET" }).then((data) =>
        (Array.isArray(data) ? data : [data]).filter(Boolean).map(normalizeBankLookupItem),
    );
};

export const showReportExecutionTimes = () =>
    request(`${resource}/getReportExecutionTimes`, { method: "GET" }).then((data) => {
        if (data?.executionTimes && data.executionTimes.length === 0) {
            return { executionTimes: [{ executionTime: "08:00:00" }, { executionTime: "16:00:00" }] };
        } else {
            return data;
        }
    });

export const initKonturAuthorization = (params: {}) => request(`${resource}/initKonturAuthorization`, body(params));
export const connectKontur = (params: Record<string, string>): Promise<KonturIntegrationResponse> =>
    request(`api/integration/ConnectKontur${queryParams(params)}`, body({}));
export const cancelKonturIntegration = () => request(`${resource}/cancelKonturIntegration`, body({}));
export const getKonturAuthorizationState = (): Promise<KonturIntegrationResponse> =>
    request(`${resource}/getKonturAuthorizationState`, { method: "GET" });

export const showFuelLimitOption = () => request(`${resource}/getFuelLimitOption`, { method: "GET" });
export const showWorkRestInfo = () => request(`${resource}/getWorkRestInformation`, { method: "GET" });
export const updateOrgAvatar = (params: {}) => alertRequest(`${resource}/editAvatar`, params);
export const uploadSigningFiles = (params: {}) => alertRequest(`${resource}/uploadSigningFiles`, params);
export const uploadAccountantSigningFiles = (params: {}) =>
    alertRequest(`${resource}/uploadAccountantSigningFiles`, params);
export const editAccountant = (params: {}) => alertRequest(`${resource}/editAccountant`, body(params));
export const updateWorkRestInfo = (params: {}) => alertRequest(`${resource}/editWorkRestInformation`, body(params));
export const delOrgAvatar = (params: {}) => alertRequest(`${resource}/clearAvatar`, body(params));
export const updateFinancial = (params: { kpp: string | null }) => {
    if (params.kpp === "") {
        params.kpp = null;
    }
    return alertRequest(`${resource}/editFinancial`, body(params));
};
export const startDemoPeriod = () => request(`${resource}/StartDemoPeriod`, body({}));
export const deferTariffSelection = () => request(`${resource}/deferTariffSelection`, body({}));
export const updateInformation = (params: {}) => alertRequest(`${resource}/editInformation`, body(params));
export const updateCurrency = (params: {}) => alertRequest(`${resource}/editCurrency`, body(params));
export const showDriverContactListSettings = () =>
    request(`${resource}/getDriverContactListSettings`, { method: "GET" });
export const updateDriverContactListSettings = (params: {}) =>
    alertRequest(`${resource}/editDriverContactListSettings`, body(params));
export const getOrganizationContacts = () => request(`${resource}/getOrganizationContacts`, { method: "GET" });
export const editOrganizationContacts = (params: {}) =>
    alertRequest(`${resource}/editOrganizationContacts`, body(params));
export const updateReportExecutionTimes = (params: {}) =>
    alertRequest(`${resource}/applyReportExecutionTimes`, body(params));
export const getOverallMailReportUsers = () =>
    request(`${resource}/GetOverallMailReportUsers`, { method: "GET" });
export const addUserToSendOverallMailReport = (params: { userId: number }) =>
    alertRequest(`${resource}/AddUserToSendOverallMailReport`, body(params));
export const removeUserFromSendOverallMailReport = (params: { userId: number }) =>
    alertRequest(`${resource}/RemoveUserFromSendOverallMailReport`, body(params));
export const showControlPointsSettings = () => request(`${resource}/getDriverControlPointsSettings`, { method: "GET" });
export const updateControlPointsSettings = (params: {}) =>
    alertRequest(`${resource}/editDriverControlPointsSettings`, body(params));
export const showDefaultRouteOptions = () =>
    request(`${resource}/getDefaultRouteOptions`, { method: "GET" }).then(getClientRouteOptions);
export const updateDefaultRouteOptions = (params: {}) =>
    alertRequest(`${resource}/editDefaultRouteOptions`, body(getClientRouteOptions(params)));
export const showProfitabilityOfBidSettings = () =>
    request(`${resource}/getProfitabilityOfBidSettings`, { method: "GET" });
export const updateProfitabilityOfBidSettings = (params: {}) =>
    alertRequest(`${resource}/editProfitabilityOfBidSettings`, body(params));
export const showSyncSystems = () =>
    request(`api/syncSystems/get`, { method: "GET" }).then((data) => ({ syncSystems: data }));
export const updateSyncSystems = (params: {}) => alertRequest(`api/syncSystems/apply`, body(params));
export const showBidCarsPlanningSettings = () => request(`${resource}/getBidCarsPlanningSettings`, { method: "GET" });
export const updateBidCarsPlanningSettings = (params: {}) =>
    alertRequest(`${resource}/editBidCarsPlanningSettings`, body(params));
export const deleteSyncSystems = (params: {}) => alertRequest(`api/syncSystems/delete`, body(params));
export const showDriverEducationResourceSettings = () =>
    request(`${resource}/getDriverEducationResourceSettings`, { method: "GET" });
export const updateDriverEducationResourceSettings = (params: {}) =>
    alertRequest(`${resource}/editDriverEducationResourceSettings`, body(params));
export const showChatsSettings = () => request(`${resource}/getChatsSettings`, { method: "GET" });
export const updateChatsSettings = (params: {}) => alertRequest(`${resource}/editChatsSettings`, body(params));
export const showFuelLimitSettings = () => request(`${resource}/getFuelLimitSettings`, { method: "GET" });
export const updateFuelLimitSettings = (params: {}) => alertRequest(`${resource}/editFuelLimitSettings`, body(params));
export const showExploitationSettings = () => request(`${resource}/getExploitationSettings`, { method: "GET" });
export const showRefuelingSettings = () => request(`${resource}/getRefuelingSettings`, { method: "GET" });
export const getCrStartSettings = () => request(`${resource}/getCrStartSettings`, { method: "GET" });
export const updateExploitationSettings = (params: {}) =>
    alertRequest(`${resource}/editExploitationSettings`, body(params));
export const showDocumentAssignmentSettings = () =>
    request(`${resource}/getDocumentAssignmentSettings`, { method: "GET" });
export const updateDocumentAssignmentSettings = (params: {}) =>
    alertRequest(`${resource}/editDocumentAssignmentSettings`, body(params));
export const getParcelProviderSettings = () => request(`${resource}/getParcelProviderSettings`, { method: "GET" });
export const editParcelProviderSettings = (params: {}) =>
    alertRequest(`${resource}/editParcelProviderSettings`, body(params));
export const checkRussianPostConnection = () =>
    alertRequest(`${resource}/checkRussianPostConnection`, { method: "GET" });
export const getLogo = () => request(`${resource}/getLogo`, { method: "GET" }, { textBody: true });
export const editLogo = (params: {}) => alertRequest(`${resource}/editLogo`, params);
export const clearLogo = (params: {}) => alertRequest(`${resource}/clearLogo`, body(params));
